import React, { useEffect, useRef, useState } from "react";
import { FaCheck } from "react-icons/fa";
import { IoHeadsetOutline } from "react-icons/io5";
import "./OnboardingHelpDemo.css";

const COPY = {
  en: {
    header: "Onboarding help",
    agent: "Ehgezly Support",
    agentMeta: "Arabic & English · free when you join",
    messages: [
      { role: "owner", text: "Hi — I'm listing my salon for the first time." },
      { role: "support", text: "Welcome! We'll walk you through setup step by step." },
      { role: "support", text: "مرحباً! سنرافقك في كل خطوة.", dir: "rtl" },
      { role: "support", text: "Profile, services & go-live — we've got you covered." },
    ],
    confirmed: "You're ready to go live!",
    summary: "Free onboarding help in Arabic & English",
  },
  ar: {
    header: "مساعدة الانضمام",
    agent: "دعم احجزلي",
    agentMeta: "عربي وإنجليزي · مجاناً عند التسجيل",
    messages: [
      { role: "owner", text: "مرحباً — أضيف صالوني لأول مرة.", dir: "rtl" },
      { role: "support", text: "أهلاً بك! سنرافقك في الإعداد خطوة بخطوة.", dir: "rtl" },
      { role: "support", text: "Welcome! We'll walk you through setup step by step." },
      { role: "support", text: "ملفك، خدماتك، وبدء النشاط — كل شيء جاهز.", dir: "rtl" },
    ],
    confirmed: "أنت جاهز للانطلاق!",
    summary: "مساعدة مجانية بالعربية والإنجليزية",
  },
};

export default function OnboardingHelpDemo({ lang = "en", featured = false }) {
  const rootRef = useRef(null);
  const [active, setActive] = useState(false);
  const [visibleCount, setVisibleCount] = useState(0);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => setActive(entry.isIntersecting),
      { threshold: 0.35 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!active) {
      setVisibleCount(0);
      setShowConfirm(false);
      return undefined;
    }

    let cancelled = false;
    const timers = [];
    const copy = COPY[lang] || COPY.en;
    const total = copy.messages.length;

    const run = () => {
      if (cancelled) return;
      setVisibleCount(0);
      setShowConfirm(false);

      for (let i = 1; i <= total; i += 1) {
        timers.push(setTimeout(() => !cancelled && setVisibleCount(i), 500 + i * 900));
      }

      timers.push(
        setTimeout(() => !cancelled && setShowConfirm(true), 500 + (total + 1) * 900)
      );
      timers.push(setTimeout(() => !cancelled && run(), 500 + (total + 3) * 900));
    };

    run();
    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [active, lang]);

  const copy = COPY[lang] || COPY.en;
  const messages = copy.messages.slice(0, visibleCount);

  return (
    <div
      ref={rootRef}
      className={`ohd-demo${featured ? " ohd-demo--featured" : ""}${active ? " ohd-demo--active" : ""}${showConfirm ? " ohd-demo--confirmed" : ""}`}
      aria-hidden="true"
    >
      <div className="ohd-stage">
        <div className="ohd-panel">
          <div className="ohd-panel-head">
            <p className="ohd-panel-label">{copy.header}</p>
          </div>
          <div className="ohd-panel-body">
            <div className="ohd-agent">
              <span className="ohd-agent-avatar" aria-hidden="true">
                <IoHeadsetOutline />
              </span>
              <div className="ohd-agent-info">
                <strong className="ohd-agent-name">{copy.agent}</strong>
                <span className="ohd-agent-meta">{copy.agentMeta}</span>
              </div>
              <div className="ohd-lang-badges" aria-hidden="true">
                <span className="ohd-lang-badge ohd-lang-badge--en">EN</span>
                <span className="ohd-lang-badge ohd-lang-badge--ar">AR</span>
              </div>
            </div>

            <div className="ohd-thread">
              {messages.map((msg, i) => (
                <div
                  key={`${msg.text}-${i}`}
                  className={`ohd-bubble ohd-bubble--${msg.role}${msg.dir === "rtl" ? " ohd-bubble--rtl" : ""}`}
                  style={{ animationDelay: `${i * 0.05}s` }}
                >
                  {msg.text}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className={`ohd-confirm${showConfirm ? " ohd-confirm--show" : ""}`}>
          <span className="ohd-confirm-icon">
            <FaCheck aria-hidden="true" />
          </span>
          <strong className="ohd-confirm-title">{copy.confirmed}</strong>
          <span className="ohd-confirm-summary">{copy.summary}</span>
        </div>
      </div>
    </div>
  );
}
